import React, { useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, CircleUserRound } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useMenu } from '@/context/MenuContext';
import { getMenuIcon, findFirstPath } from '@/lib/menuUtils';

/** 탭 하나의 공통 모양 — 모듈 탭과 My Page 탭이 같은 생김새를 쓴다. */
const TAB_BASE =
  'group relative flex shrink-0 items-center gap-2 whitespace-nowrap px-4 py-3 text-sm font-semibold transition-colors';

/**
 * GNB — 상단 모듈 스위처.
 * 권한 필터링된 메뉴 트리의 최상위 노드가 곧 모듈이다.
 * 모듈이 늘어나면(전자결재/자원/인사) 코드 수정 없이 자동으로 탭이 추가된다.
 *
 * My Page 만 예외로 여기에 붙박이로 둔다.
 * 자기 정보·알림 설정·보안 센터라 권한과 무관하게 누구나 열 수 있어야 하고,
 * 메뉴 표에 넣으면 역할마다 권한 행을 따로 심어야 한다.
 */
const ModuleBar = () => {
  const { modules, activeModule, badges } = useMenu();
  const scrollerRef = useRef(null);
  const { pathname } = useLocation();

  if (!modules.length) return null;

  const isMyPage = pathname.startsWith('/mypage');

  const scrollBy = (offset) => {
    scrollerRef.current?.scrollBy({ left: offset, behavior: 'smooth' });
  };

  return (
    <div className="relative hidden lg:flex items-center border-b border-chrome-border bg-chrome-2 px-2">
      <button
        type="button"
        onClick={() => scrollBy(-240)}
        className="shrink-0 rounded p-1 text-chrome-muted transition-colors hover:bg-chrome-hover hover:text-chrome-strong"
        aria-label="이전 모듈"
      >
        <ChevronLeft size={16} />
      </button>

      <nav
        ref={scrollerRef}
        className="flex flex-1 items-stretch gap-1 overflow-x-auto scrollbar-hide"
      >
        {modules.map((module) => {
          const to = findFirstPath(module) || '/dashboard';
          const isActive = activeModule?.menuId === module.menuId;
          const badgeCount = module.badgeKey ? badges[module.badgeKey] : 0;

          return (
            <Link
              key={module.menuId}
              to={to}
              className={cn(
                TAB_BASE,
                // 선택된 모듈은 밑줄만으로는 눈에 잘 안 들어와 옅은 탭 배경을 함께 깐다.
                isActive
                  ? 'rounded-t-md bg-chrome-active text-chrome-active-text'
                  : 'text-chrome-muted hover:bg-chrome-hover hover:text-chrome-text'
              )}
            >
              <span className={cn(isActive ? 'text-chrome-accent' : 'text-chrome-muted group-hover:text-chrome-text')}>
                {getMenuIcon(module.iconClass, 18)}
              </span>
              {module.menuName}

              {badgeCount > 0 && (
                <span className="ml-0.5 rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
                  {badgeCount > 99 ? '99+' : badgeCount}
                </span>
              )}

              <span
                className={cn(
                  'absolute inset-x-0 bottom-0 h-[3px] rounded-t-full transition-colors',
                  isActive ? 'bg-chrome-accent' : 'bg-transparent'
                )}
              />
            </Link>
          );
        })}

        {/* My Page — 권한과 무관하게 늘 열리는 내 자리 */}
        <Link
          to="/mypage"
          className={cn(
            TAB_BASE,
            'ml-1 border-l border-chrome-border pl-4',
            isMyPage
              ? 'rounded-t-md bg-chrome-active text-chrome-active-text'
              : 'text-chrome-muted hover:bg-chrome-hover hover:text-chrome-text'
          )}
        >
          <span className={cn(isMyPage ? 'text-chrome-accent' : 'text-chrome-muted group-hover:text-chrome-text')}>
            <CircleUserRound size={18} />
          </span>
          My Page
          <span
            className={cn(
              'absolute inset-x-0 bottom-0 h-[3px] rounded-t-full transition-colors',
              isMyPage ? 'bg-chrome-accent' : 'bg-transparent'
            )}
          />
        </Link>
      </nav>

      <button
        type="button"
        onClick={() => scrollBy(240)}
        className="shrink-0 rounded p-1 text-chrome-muted transition-colors hover:bg-chrome-hover hover:text-chrome-strong"
        aria-label="다음 모듈"
      >
        <ChevronRight size={16} />
      </button>
    </div>
  );
};

export default ModuleBar;
